import { itemAad } from "@passwordflow/crypto";
import {
  acceptInvite,
  claimInvite,
  createInvite,
  createOrg,
  fetchOwnKey,
  fetchUserPublicKey,
  listOrgMembers,
  listRotationItems,
  lookupInvite,
  putOwnKey,
  rotateOrg
} from "../api/teams.js";
import type { InviteLookup, OrgDetail, RotationItem, RotationMember, TeamItemFull, TeamRole } from "../api/teams.js";
import { b64ToUtf8, bytesToB64, utf8ToB64 } from "../b64.js";
import type { CryptoClient } from "../cryptoClient.js";
import { parsePayload, serializePayload } from "../vaultPayload.js";
import type { VaultPayload } from "../vaultPayload.js";

const identityReady = new WeakMap<CryptoClient, Promise<string>>();
const orgKeyReady = new WeakMap<CryptoClient, Map<string, Promise<void>>>();

/**
 * Makes sure the worker holds our identity key. The first team action creates the keypair and uploads it,
 * with the private half wrapped by the wrap key. Returns our public key.
 */
export function ensureIdentity(client: CryptoClient): Promise<string> {
  const cached = identityReady.get(client);
  if (cached !== undefined) {
    return cached;
  }
  const pending = (async (): Promise<string> => {
    const own = await fetchOwnKey();
    if (own === undefined) {
      const created = await client.identityCreate();
      await putOwnKey(created.pubB64, created.privEncB64);
      return created.pubB64;
    }
    await client.identityLoad(own.identityPubB64, own.identityPrivEncB64);
    return own.identityPubB64;
  })();
  identityReady.set(client, pending);
  pending.catch((): void => {
    identityReady.delete(client);
  });
  return pending;
}

function orgKeyMap(client: CryptoClient): Map<string, Promise<void>> {
  const existing = orgKeyReady.get(client);
  if (existing !== undefined) {
    return existing;
  }
  const created = new Map<string, Promise<void>>();
  orgKeyReady.set(client, created);
  return created;
}

/** Loads the org key for this key version into the worker. Idempotent per (org, version). */
export function ensureOrgKey(client: CryptoClient, org: Pick<OrgDetail, "id" | "keyVersion" | "wrappedOrgKeyB64">): Promise<void> {
  const map = orgKeyMap(client);
  const key = `${org.id}:${org.keyVersion}`;
  const cached = map.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const pending = ensureIdentity(client).then((): Promise<void> => client.orgOpen(org.id, org.wrappedOrgKeyB64));
  map.set(key, pending);
  pending.catch((): void => {
    map.delete(key);
  });
  return pending;
}

function markOrgKeyReady(client: CryptoClient, orgId: string, keyVersion: number): void {
  orgKeyMap(client).set(`${orgId}:${keyVersion}`, Promise.resolve());
}

export function teamAad(orgId: string, itemId: string): string {
  return bytesToB64(itemAad(orgId, itemId));
}

/** Creates a team with a fresh org key. The key never leaves the worker in plain form. */
export async function createTeam(client: CryptoClient, name: string): Promise<string> {
  await ensureIdentity(client);
  const orgId = crypto.randomUUID();
  const wrappedOrgKeyB64 = await client.orgCreate(orgId);
  await createOrg({ id: orgId, name, wrappedOrgKeyB64 });
  markOrgKeyReady(client, orgId, 1);
  return orgId;
}

/** Wraps the org key under a fresh invite secret. The secret goes into the link; the server stores only the wrap. */
export async function inviteMember(
  client: CryptoClient,
  org: OrgDetail,
  email: string,
  role: TeamRole
): Promise<{ secretB64: string; expiresAt: number; email: string; role: TeamRole }> {
  await ensureOrgKey(client, org);
  const material = await client.orgInviteCreate(org.id);
  const invite = await createInvite(org.id, {
    email,
    role,
    tokenB64: material.tokenB64,
    wrappedOrgKeyB64: material.wrappedB64
  });
  return { secretB64: material.secretB64, expiresAt: invite.expiresAt, email: invite.email, role: invite.role };
}

export interface InviteAccepted {
  readonly orgId: string;
  readonly orgName: string;
  readonly role: TeamRole;
}

/** Reads an invite with its secret, then joins the team with our own wrap of the org key. */
export async function previewInvite(client: CryptoClient, secretB64: string): Promise<InviteLookup> {
  const token = await client.inviteDerive(secretB64);
  return lookupInvite(token);
}

export async function joinWithInvite(client: CryptoClient, secretB64: string, lookup: InviteLookup): Promise<InviteAccepted> {
  const ownPub = await ensureIdentity(client);
  await client.orgOpenInvite(lookup.orgId, secretB64, lookup.wrappedOrgKeyB64);
  const wrappedOrgKeyB64 = await client.orgSealFor(lookup.orgId, ownPub);
  const token = await client.inviteDerive(secretB64);
  const accepted = await acceptInvite(token, wrappedOrgKeyB64);
  return { orgId: accepted.orgId, orgName: lookup.orgName, role: accepted.role };
}

/**
 * Binds the invite to this account without joining yet: the org key is re-wrapped to our identity key and
 * parked on the invite, so it can be accepted later from the pending list on any device, without the link.
 */
export async function claimWithInvite(client: CryptoClient, secretB64: string, lookup: InviteLookup): Promise<void> {
  const ownPub = await ensureIdentity(client);
  await client.orgOpenInvite(lookup.orgId, secretB64, lookup.wrappedOrgKeyB64);
  const wrappedOrgKeyB64 = await client.orgSealFor(lookup.orgId, ownPub);
  const token = await client.inviteDerive(secretB64);
  await claimInvite(token, wrappedOrgKeyB64);
}

/**
 * Re-keys the team after a membership change. Every remaining member gets a new wrap, and every item
 * (including trashed ones) is re-encrypted. Failure leaves the old key in place and aborts the pending key.
 */
export async function rotateTeamKey(client: CryptoClient, org: OrgDetail): Promise<number> {
  await ensureOrgKey(client, org);
  const [members, items] = await Promise.all([listOrgMembers(org.id), listRotationItems(org.id)]);
  await client.orgRotateBegin(org.id);
  try {
    const wrappedKeys: RotationMember[] = [];
    for (const member of members) {
      const recipientPub = await fetchUserPublicKey(member.userId);
      const wrappedOrgKeyB64 = await client.orgRotateSealFor(org.id, recipientPub);
      wrappedKeys.push({ userId: member.userId, wrappedOrgKeyB64 });
    }
    const reencrypted: RotationItem[] = [];
    for (const item of items) {
      const sealed = await client.orgRotateReencrypt(org.id, item.nonceB64, item.ciphertextB64, teamAad(org.id, item.id));
      reencrypted.push({
        collectionId: item.collectionId,
        itemId: item.id,
        expectedVersion: item.version,
        ciphertextB64: sealed.ciphertextB64,
        nonceB64: sealed.nonceB64
      });
    }
    const result = await rotateOrg(org.id, { wrappedKeys, items: reencrypted });
    await client.orgRotateCommit(org.id);
    markOrgKeyReady(client, org.id, result.keyVersion);
    return result.keyVersion;
  } catch (err: unknown) {
    await client.orgRotateAbort(org.id).catch((): void => undefined);
    throw err;
  }
}

export async function decryptTeamPayload(client: CryptoClient, orgId: string, item: TeamItemFull): Promise<VaultPayload | undefined> {
  const plain = await client.orgDecrypt(orgId, item.nonceB64, item.ciphertextB64, teamAad(orgId, item.id));
  return parsePayload(b64ToUtf8(plain));
}

export async function encryptTeamPayload(
  client: CryptoClient,
  orgId: string,
  itemId: string,
  payload: VaultPayload
): Promise<{ ciphertextB64: string; nonceB64: string; titleHash: string }> {
  const sealed = await client.orgEncrypt(orgId, utf8ToB64(serializePayload(payload)), teamAad(orgId, itemId));
  const titleHash = await client.titleHash(orgId, payload.title);
  return { ciphertextB64: sealed.ciphertextB64, nonceB64: sealed.nonceB64, titleHash };
}
